import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { AiBlock, hasVisualBlocks, parseAiMessage } from "../../utils/aiMessageParser";
import "./AiMessageBlocks.css";

const BAR_COLORS = [
    "#007aff",
    "#34c759",
    "#ff9500",
    "#ff3b30",
    "#af52de",
    "#00a7a0",
    "#e0a800",
    "#ff6b6b",
];

interface Props {
    text: string;
}

function MarkdownBlock({ content }: { content: string }) {
    return (
        <div className="ai-markdown-block">
            <ReactMarkdown>{content}</ReactMarkdown>
        </div>
    );
}

function TableBlock({ block }: { block: Extract<AiBlock, { type: "table" }> }) {
    return (
        <div className="ai-visual-card ai-table-scroll">
            <table className="ai-data-table">
                <thead>
                    <tr>
                        {block.headers.map((header, index) => <th key={`${header}-${index}`}>{header}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {block.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {block.headers.map((_, cellIndex) => (
                                <td key={cellIndex}>{row[cellIndex] ?? ""}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function BarChartBlock({ block }: { block: Extract<AiBlock, { type: "bar_chart" }> }) {
    const max = Math.max(...block.items.map(item => item.value), 1);
    return (
        <div className="ai-visual-card ai-chart-card">
            {block.title && <div className="ai-visual-card-title">{block.title}</div>}
            <div className="ai-chart-list">
                {block.items.map((item, index) => {
                    const percentage = Math.max((item.value / max) * 100, 2);
                    return (
                        <div className="ai-chart-row" key={`${item.label}-${index}`}>
                            <span className="ai-chart-label" title={item.label}>{item.label}</span>
                            <span className="ai-chart-track" aria-hidden="true">
                                <span
                                    className="ai-chart-fill"
                                    style={{ width: `${percentage}%`, backgroundColor: BAR_COLORS[index % BAR_COLORS.length] }}
                                />
                            </span>
                            <span className="ai-chart-value">{item.raw}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ComparisonBlock({ block }: { block: Extract<AiBlock, { type: "comparison" }> }) {
    return (
        <div className="ai-visual-card">
            {block.title && <div className="ai-visual-card-title">{block.title}</div>}
            <div className="ai-comparison-list">
                {block.items.map((item, index) => (
                    <div className="ai-comparison-row" key={`${item.label}-${index}`}>
                        <span className="ai-comparison-index">{index + 1}</span>
                        <span className="ai-comparison-info">
                            <strong>{item.label}</strong>
                            {item.note && <small>{item.note}</small>}
                        </span>
                        <span className="ai-comparison-value">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function InfoCardBlock({ block }: { block: Extract<AiBlock, { type: "info_card" }> }) {
    return (
        <div className="ai-visual-card">
            {block.title && <div className="ai-visual-card-title">{block.title}</div>}
            <dl className="ai-info-list">
                {block.lines.map((line, index) => (
                    <div className="ai-info-row" key={`${line.key}-${index}`}>
                        <dt>{line.key}</dt>
                        <dd>{line.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

function renderBlock(block: AiBlock, index: number) {
    switch (block.type) {
        case "text":
            return <MarkdownBlock key={index} content={block.content} />;
        case "table":
            return <TableBlock key={index} block={block} />;
        case "bar_chart":
            return <BarChartBlock key={index} block={block} />;
        case "comparison":
            return <ComparisonBlock key={index} block={block} />;
        case "info_card":
            return <InfoCardBlock key={index} block={block} />;
        default:
            return null;
    }
}

export default function AiMessageContent({ text }: Props) {
    const blocks = useMemo(() => parseAiMessage(text), [text]);
    const visual = useMemo(() => hasVisualBlocks(blocks), [blocks]);

    if (blocks.length === 0) return null;

    return (
        <div className={`ai-message-content${visual ? " ai-message-content--visual" : ""}`}>
            {blocks.map(renderBlock)}
        </div>
    );
}
